/**
 * @fileoverview KOL referral system service layer
 * @module features/referrals/services/referralService
 *
 * Provides all database operations for the KOL referral system including:
 * - Promotion management (start/stop promoting projects)
 * - Wallet attribution (save/get referral attributions by wallet)
 * - Investment tracking (record investments for analytics)
 * - Validation (check if KOL is promoting project)
 */

import { supabase } from '@/lib/supabase';

/**
 * Database row type for kol_project_promotions table.
 */
export interface KOLProjectPromotion {
  id: string;
  kol_id: string;
  fundraiser_id: string;
  is_active: boolean;
  started_at: string;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Database row type for wallet_referral_attributions table.
 */
export interface WalletReferralAttribution {
  id: string;
  wallet_address: string;
  kol_id: string;
  kol_slug: string;
  user_id: string | null;
  referral_source: string;
  first_click_at: string;
  last_click_at: string;
  created_at: string;
  updated_at: string;
}

/**
 * Database row type for user_referral_attributions table.
 */
export interface UserReferralAttribution {
  id: string;
  user_id: string;
  kol_id: string;
  kol_slug: string;
  referral_source: string;
  first_click_at: string;
  last_click_at: string;
  created_at: string;
  updated_at: string;
}

/**
 * Database row type for kol_referral_investments table.
 */
export interface KOLReferralInvestment {
  id: string;
  kol_id: string;
  fundraiser_id: string;
  investor_wallet_address: string;
  amount_wei: string;
  amount_usd: number;
  commission_rate: number;
  estimated_commission_wei: string;
  estimated_commission_usd: number;
  transaction_hash: string;
  block_number: number | null;
  status: 'confirmed' | 'paid' | 'failed';
  invested_at: string;
  created_at: string;
  updated_at: string;
}

/**
 * Input type for creating a new promotion.
 */
export interface CreatePromotionInput {
  kol_id: string;
  fundraiser_id: string;
  /** Dynamic.xyz user ID for authentication (required for Dynamic users) */
  dynamic_user_id?: string;
}

/**
 * Input type for saving wallet attribution.
 */
export interface SaveAttributionInput {
  wallet_address: string;
  kol_id: string;
  kol_slug: string;
  user_id?: string;
  referral_source?: string;
}

/**
 * Input type for saving user attribution.
 */
export interface SaveUserAttributionInput {
  user_id: string;
  kol_id: string;
  kol_slug: string;
  referral_source?: string;
}

/**
 * Input type for recording KOL investment.
 */
export interface RecordInvestmentInput {
  kol_id: string;
  fundraiser_id: string;
  investor_wallet_address: string;
  amount_wei: string;
  amount_usd: number;
  commission_rate: number;
  estimated_commission_wei: string;
  estimated_commission_usd: number;
  transaction_hash: string;
  block_number?: number;
}

// ============================================================================
// PROMOTION MANAGEMENT
// ============================================================================

/**
 * Gets all active promotions for a specific KOL.
 *
 * Returns fundraiser IDs that the KOL is currently promoting.
 * Used to display "Currently Promoting" section on KOL profile page.
 *
 * @param kolId - UUID of the KOL
 * @returns Array of active promotion records
 * @throws Error if database query fails
 *
 * @example
 * ```typescript
 * const promotions = await getKOLActivePromotions('123e4567-e89b-12d3-a456-426614174000');
 * console.log(promotions.map(p => p.fundraiser_id)); // ['fundraiser-1', 'fundraiser-2']
 * ```
 */
export async function getKOLActivePromotions(
  kolId: string
): Promise<KOLProjectPromotion[]> {
  const { data, error } = await supabase
    .from('kol_project_promotions')
    .select('*')
    .eq('kol_id', kolId)
    .eq('is_active', true)
    .order('started_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch KOL promotions: ${error.message}`);
  }

  return data || [];
}

/**
 * Creates or retrieves an existing promotion for a KOL.
 *
 * Starts promoting a specific project. KOL must be promoting for commission eligibility.
 * If an active promotion already exists for this KOL + fundraiser, returns it instead.
 * If an inactive promotion exists, reactivates it.
 *
 * For Dynamic.xyz users, pass the `dynamic_user_id` to use secure RPC function.
 *
 * @param input - Promotion creation data
 * @returns Created or existing promotion record
 * @throws Error if database operation fails
 *
 * @example
 * ```typescript
 * const promotion = await createPromotion({
 *   kol_id: '123e4567-e89b-12d3-a456-426614174000',
 *   fundraiser_id: '987fcdeb-51a2-43d7-b890-123456789abc',
 *   dynamic_user_id: 'dynamic-user-id' // For Dynamic.xyz users
 * });
 * ```
 */
export async function createPromotion(
  input: CreatePromotionInput
): Promise<KOLProjectPromotion> {
  // For Dynamic.xyz users, use secure RPC function
  if (input.dynamic_user_id) {
    const { data: promotionId, error: rpcError } = await supabase.rpc('create_kol_promotion', {
      p_dynamic_user_id: input.dynamic_user_id,
      p_fundraiser_id: input.fundraiser_id,
    });

    if (rpcError) {
      throw new Error(`Failed to create promotion: ${rpcError.message}`);
    }

    // Fetch the promotion record
    const { data: promotion, error: fetchError } = await supabase
      .from('kol_project_promotions')
      .select('*')
      .eq('id', promotionId)
      .single();

    if (fetchError) {
      throw new Error(`Failed to fetch promotion: ${fetchError.message}`);
    }

    return promotion;
  }

  // Legacy path for Supabase Auth users
  // First, check if an active promotion already exists
  const { data: existingPromotion, error: checkError } = await supabase
    .from('kol_project_promotions')
    .select('*')
    .eq('kol_id', input.kol_id)
    .eq('fundraiser_id', input.fundraiser_id)
    .eq('is_active', true)
    .maybeSingle();

  if (checkError) {
    throw new Error(`Failed to check existing promotion: ${checkError.message}`);
  }

  // If active promotion exists, return it
  if (existingPromotion) {
    return existingPromotion;
  }

  // Check if an inactive promotion exists that we should reactivate
  const { data: inactivePromotion, error: inactiveError } = await supabase
    .from('kol_project_promotions')
    .select('*')
    .eq('kol_id', input.kol_id)
    .eq('fundraiser_id', input.fundraiser_id)
    .eq('is_active', false)
    .maybeSingle();

  if (inactiveError) {
    throw new Error(`Failed to check inactive promotion: ${inactiveError.message}`);
  }

  // If inactive promotion exists, reactivate it
  if (inactivePromotion) {
    const { data: reactivated, error: reactivateError } = await supabase
      .from('kol_project_promotions')
      .update({
        is_active: true,
        started_at: new Date().toISOString(),
        ended_at: null,
      })
      .eq('id', inactivePromotion.id)
      .select()
      .single();

    if (reactivateError) {
      throw new Error(`Failed to reactivate promotion: ${reactivateError.message}`);
    }

    return reactivated;
  }

  // No existing promotion, create new one
  const { data, error } = await supabase
    .from('kol_project_promotions')
    .insert({
      kol_id: input.kol_id,
      fundraiser_id: input.fundraiser_id,
      is_active: true,
    })
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create promotion: ${error.message}`);
  }

  return data;
}

/**
 * Ends an active promotion for a KOL.
 *
 * Marks the promotion as inactive and sets ended_at timestamp.
 * After this, KOL will no longer receive commissions for this project.
 *
 * @param kolId - UUID of the KOL
 * @param fundraiserId - UUID of the fundraiser
 * @returns Updated promotion record
 * @throws Error if database update fails or promotion not found
 *
 * @example
 * ```typescript
 * const endedPromotion = await endPromotion(
 *   '123e4567-e89b-12d3-a456-426614174000',
 *   '987fcdeb-51a2-43d7-b890-123456789abc'
 * );
 * ```
 */
export async function endPromotion(
  kolId: string,
  fundraiserId: string
): Promise<KOLProjectPromotion> {
  const { data, error } = await supabase
    .from('kol_project_promotions')
    .update({
      is_active: false,
      ended_at: new Date().toISOString(),
    })
    .eq('kol_id', kolId)
    .eq('fundraiser_id', fundraiserId)
    .eq('is_active', true)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to end promotion: ${error.message}`);
  }

  return data;
}

/**
 * Checks if a KOL is currently promoting a specific project.
 *
 * CRITICAL for commission eligibility validation.
 * Called before passing KOL address to smart contract.
 *
 * @param kolId - UUID of the KOL
 * @param fundraiserId - UUID of the fundraiser
 * @returns true if KOL has active promotion, false otherwise
 *
 * @example
 * ```typescript
 * const isPromoting = await isKOLPromoting(kolId, fundraiserId);
 * if (!isPromoting) {
 *   return '0x0000000000000000000000000000000000000000'; // Zero address = no commission
 * }
 * return kol.wallet_address;
 * ```
 */
export async function isKOLPromoting(
  kolId: string,
  fundraiserId: string
): Promise<boolean> {

  const { data, error } = await supabase
    .from('kol_project_promotions')
    .select('id, is_active, started_at')
    .eq('kol_id', kolId)
    .eq('fundraiser_id', fundraiserId)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    return false;
  }

  const isPromoting = data !== null;

  return isPromoting;
}

// ============================================================================
// KOL LOOKUP
// ============================================================================

/**
 * Gets KOL wallet address by slug.
 *
 * Converts URL-friendly slug to Ethereum wallet address for smart contract.
 * Returns null if KOL not found or wallet not assigned.
 *
 * @param slug - KOL slug from referral link (e.g., "crypto_expert")
 * @returns KOL wallet address or null
 *
 * @example
 * ```typescript
 * const walletAddress = await getKOLWalletBySlug('crypto_expert');
 * if (walletAddress) {
 *   // Use wallet address in smart contract call
 * }
 * ```
 */
export async function getKOLWalletBySlug(
  slug: string
): Promise<string | null> {
  const { data, error } = await supabase
    .from('kols')
    .select('wallet_address')
    .eq('slug', slug)
    .maybeSingle();

  if (error) {
    return null;
  }

  return data?.wallet_address || null;
}

/**
 * Gets full KOL data by slug.
 *
 * Returns complete KOL record including ID and wallet address.
 * Used when both KOL ID and wallet are needed.
 *
 * @param slug - KOL slug from referral link
 * @returns KOL record or null
 *
 * @example
 * ```typescript
 * const kol = await getKOLBySlug('crypto_expert');
 * if (kol) {
 *   await saveWalletAttribution(walletAddress, kol.id, slug);
 * }
 * ```
 */
export async function getKOLBySlug(slug: string): Promise<{
  id: string;
  wallet_address: string | null;
  slug: string;
} | null> {
  const { data, error } = await supabase
    .from('kols')
    .select('id, wallet_address, slug')
    .eq('slug', slug)
    .maybeSingle();

  if (error) {
    return null;
  }

  return data;
}

// ============================================================================
// WALLET ATTRIBUTION
// ============================================================================

/**
 * Saves or updates wallet referral attribution.
 *
 * Implements last-click attribution: overwrites previous attribution.
 * Wallet address stored in lowercase for consistency.
 *
 * Used when:
 * - User clicks referral link and connects wallet
 * - Migrating from localStorage to database
 *
 * @param input - Attribution data
 * @returns Saved attribution record
 * @throws Error if database upsert fails
 *
 * @example
 * ```typescript
 * await saveWalletAttribution({
 *   wallet_address: '0x1234...5678',
 *   kol_id: '123e4567-e89b-12d3-a456-426614174000',
 *   kol_slug: 'crypto_expert',
 *   referral_source: 'link'
 * });
 * ```
 */
export async function saveWalletAttribution(
  input: SaveAttributionInput
): Promise<WalletReferralAttribution> {
  // Convert wallet address to lowercase for consistency
  const walletLower = input.wallet_address.toLowerCase();

  const { data, error} = await supabase
    .from('wallet_referral_attributions')
    .upsert(
      {
        wallet_address: walletLower,
        kol_id: input.kol_id,
        kol_slug: input.kol_slug,
        user_id: input.user_id || null,
        referral_source: input.referral_source || 'link',
        first_click_at: new Date().toISOString(),
        last_click_at: new Date().toISOString(),
      },
      {
        onConflict: 'wallet_address', // Update if wallet already exists
      }
    )
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to save wallet attribution: ${error.message}`);
  }

  return data;
}

/**
 * Gets current referral attribution for a wallet.
 *
 * Returns the KOL that should receive commission when this wallet invests.
 * Uses lowercase wallet address for case-insensitive lookup.
 *
 * @param walletAddress - Ethereum wallet address
 * @returns Attribution record or null
 *
 * @example
 * ```typescript
 * const attribution = await getWalletAttribution('0x1234...5678');
 * if (attribution) {
 *   console.log(`Referred by: ${attribution.kol_slug}`);
 * }
 * ```
 */
export async function getWalletAttribution(
  walletAddress: string
): Promise<WalletReferralAttribution | null> {
  const walletLower = walletAddress.toLowerCase();

  const { data, error } = await supabase
    .from('wallet_referral_attributions')
    .select('*')
    .eq('wallet_address', walletLower)
    .maybeSingle();

  if (error) {
    return null;
  }

  return data;
}

// ============================================================================
// USER ATTRIBUTION (PREFERRED - Cross-Device, Multi-Wallet Support)
// ============================================================================

/**
 * Saves or updates user-based referral attribution.
 *
 * PREFERRED METHOD for attribution tracking. User-based attribution provides:
 * - Cross-device tracking (user can switch devices)
 * - Multi-wallet support (user can use different wallets)
 * - Better UX (no need to reconnect wallet on new device)
 * - Follows industry best practices (Republic, CoinList model)
 *
 * Implements last-click attribution: overwrites previous attribution.
 *
 * Uses a secure database function for Dynamic.xyz compatibility.
 *
 * Used when:
 * - User logs in with Google/Twitter and has referral in localStorage
 * - User clicks referral link while already authenticated
 * - Migrating from wallet-based to user-based attribution
 *
 * @param input - User attribution data
 * @returns Saved attribution record
 * @throws Error if database upsert fails
 *
 * @example
 * ```typescript
 * await saveUserAttribution({
 *   user_id: 'auth-user-uuid',
 *   kol_id: '123e4567-e89b-12d3-a456-426614174000',
 *   kol_slug: 'crypto_expert',
 *   referral_source: 'link'
 * });
 * ```
 */
export async function saveUserAttribution(
  input: SaveUserAttributionInput
): Promise<UserReferralAttribution> {
  // Use secure function for Dynamic.xyz compatibility (bypasses RLS safely)
  // Note: user_id here is the Dynamic user ID (string), function converts to profile UUID
  const { error: rpcError } = await supabase.rpc('upsert_user_referral_attribution', {
    p_dynamic_user_id: input.user_id, // Dynamic user ID
    p_kol_id: input.kol_id,
    p_kol_slug: input.kol_slug,
    p_referral_source: input.referral_source || 'link',
  });

  if (rpcError) {
    throw new Error(`Failed to save user attribution: ${rpcError.message}`);
  }

  // Fetch the saved record to return
  const { data, error: fetchError } = await supabase.rpc('get_user_referral_attribution', {
    p_dynamic_user_id: input.user_id, // Dynamic user ID
  });

  if (fetchError) {
    throw new Error(`Failed to fetch user attribution: ${fetchError.message}`);
  }

  // RPC returns array, get first element
  const attribution = Array.isArray(data) ? data[0] : data;

  if (!attribution) {
    throw new Error('Attribution not found after save');
  }

  return attribution as UserReferralAttribution;
}

/**
 * Gets current referral attribution for a user.
 *
 * PREFERRED METHOD for attribution lookup.
 * Returns the KOL that should receive commission when this user invests.
 * Uses secure function for Dynamic.xyz compatibility.
 *
 * @param userId - User ID (profile UUID for Dynamic users)
 * @returns Attribution record or null
 *
 * @example
 * ```typescript
 * const attribution = await getUserAttribution(user.id);
 * if (attribution) {
 *   console.log(`Referred by: ${attribution.kol_slug}`);
 * }
 * ```
 */
export async function getUserAttribution(
  userId: string
): Promise<UserReferralAttribution | null> {

  // Use secure function for Dynamic.xyz compatibility
  // Note: userId here is the Dynamic user ID (string), function converts to profile UUID
  const { data, error } = await supabase.rpc('get_user_referral_attribution', {
    p_dynamic_user_id: userId, // Dynamic user ID
  });

  if (error) {
    return null;
  }

  // RPC returns array, get first element
  const attribution = Array.isArray(data) ? data[0] : data;

  return attribution as UserReferralAttribution | null;
}

/**
 * Migrates wallet-based attribution to user-based attribution.
 *
 * Called automatically when:
 * 1. User logs in with Google/Twitter
 * 2. User connects a wallet
 * 3. System finds existing wallet attribution
 *
 * This creates a seamless migration path from wallet-based to user-based
 * attribution, ensuring existing referrals are preserved.
 *
 * NOTE: Does NOT delete wallet attribution (kept for backwards compatibility).
 *
 * @param userId - Supabase auth user ID
 * @param walletAddress - Ethereum wallet address to migrate from
 * @returns Migrated user attribution or null if no wallet attribution exists
 *
 * @example
 * ```typescript
 * // On user login with wallet connected:
 * const migrated = await migrateWalletToUserAttribution(
 *   user.id,
 *   connectedWallet.address
 * );
 * if (migrated) {
 *   console.log(`Migrated referral from wallet to user: ${migrated.kol_slug}`);
 * }
 * ```
 */
export async function migrateWalletToUserAttribution(
  userId: string,
  walletAddress: string
): Promise<UserReferralAttribution | null> {
  // First, check if user attribution already exists
  const existingUserAttribution = await getUserAttribution(userId);
  if (existingUserAttribution) {
    // User already has attribution, no need to migrate
    return existingUserAttribution;
  }

  // Get wallet attribution to migrate
  const walletAttribution = await getWalletAttribution(walletAddress);
  if (!walletAttribution) {
    // No wallet attribution to migrate
    return null;
  }

  // Create user attribution from wallet attribution
  const userAttribution = await saveUserAttribution({
    user_id: userId,
    kol_id: walletAttribution.kol_id,
    kol_slug: walletAttribution.kol_slug,
    referral_source: walletAttribution.referral_source,
  });


  return userAttribution;
}

// ============================================================================
// INVESTMENT TRACKING
// ============================================================================

/**
 * Records a KOL referral investment for analytics.
 *
 * Called after successful investment when KOL was eligible for commission.
 * Populates KOL dashboard with investment metrics.
 *
 * NOTE: Does NOT handle commission payment (that's in smart contract).
 * This is purely for analytics and estimated commission display.
 *
 * @param input - Investment tracking data
 * @returns Created investment record
 * @throws Error if database insert fails
 *
 * @example
 * ```typescript
 * await recordKOLInvestment({
 *   kol_id: '123e4567-e89b-12d3-a456-426614174000',
 *   fundraiser_id: '987fcdeb-51a2-43d7-b890-123456789abc',
 *   investor_wallet_address: '0x1234...5678',
 *   amount_wei: '1000000000000000000',  // 1 ETH in wei
 *   amount_usd: 2000,
 *   commission_rate: 5.00,  // 5%
 *   estimated_commission_wei: '50000000000000000',  // 0.05 ETH
 *   estimated_commission_usd: 100,
 *   transaction_hash: '0xabcd...'
 * });
 * ```
 */
export async function recordKOLInvestment(
  input: RecordInvestmentInput
): Promise<KOLReferralInvestment> {

  // Upsert (not insert) on transaction_hash: one referral record per on-chain
  // invest tx. This keeps the client write idempotent and collision-free with
  // the server-side reconciler (reconcile-kol-referrals edge function), which
  // may have already recovered this row from on-chain truth. The unique
  // constraint kol_referral_investments_transaction_hash_key backs onConflict.
  const { data, error } = await supabase
    .from('kol_referral_investments')
    .upsert(
      {
        kol_id: input.kol_id,
        fundraiser_id: input.fundraiser_id,
        investor_wallet_address: input.investor_wallet_address.toLowerCase(),
        amount_wei: input.amount_wei,
        amount_usd: input.amount_usd,
        commission_rate: input.commission_rate,
        estimated_commission_wei: input.estimated_commission_wei,
        estimated_commission_usd: input.estimated_commission_usd,
        transaction_hash: input.transaction_hash,
        block_number: input.block_number || null,
        status: 'confirmed',
      },
      { onConflict: 'transaction_hash' }
    )
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to record KOL investment: ${error.message}`);
  }


  return data;
}

/**
 * Gets all referral investments for a specific KOL.
 *
 * Used for KOL dashboard analytics:
 * - Total investments brought
 * - Total amount raised
 * - Estimated commissions
 * - Investment timeline
 *
 * @param kolId - UUID of the KOL
 * @returns Array of investment records
 *
 * @example
 * ```typescript
 * const investments = await getKOLInvestments(kolId);
 * const totalRaised = investments.reduce((sum, inv) => sum + inv.amount_usd, 0);
 * const estimatedCommission = investments.reduce((sum, inv) => sum + inv.estimated_commission_usd, 0);
 * ```
 */
export async function getKOLInvestments(
  kolId: string
): Promise<KOLReferralInvestment[]> {
  const { data, error } = await supabase
    .from('kol_referral_investments')
    .select('*')
    .eq('kol_id', kolId)
    .order('invested_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch KOL investments: ${error.message}`);
  }

  return data || [];
}

/**
 * Gets referral investment statistics for a KOL.
 *
 * Aggregated metrics for dashboard display:
 * - Total number of investments
 * - Total amount raised (USD)
 * - Total estimated commissions (USD)
 * - Number of unique investors
 *
 * @param kolId - UUID of the KOL
 * @returns Investment statistics
 *
 * @example
 * ```typescript
 * const stats = await getKOLInvestmentStats(kolId);
 * console.log(`Total raised: $${stats.totalAmountUsd}`);
 * console.log(`Estimated commission: $${stats.totalCommissionUsd}`);
 * ```
 */
export async function getKOLInvestmentStats(kolId: string): Promise<{
  totalInvestments: number;
  totalAmountUsd: number;
  totalCommissionUsd: number;
  uniqueInvestors: number;
}> {
  const { data, error } = await supabase
    .from('kol_referral_investments')
    .select('amount_usd, estimated_commission_usd, investor_wallet_address')
    .eq('kol_id', kolId);

  if (error) {
    return {
      totalInvestments: 0,
      totalAmountUsd: 0,
      totalCommissionUsd: 0,
      uniqueInvestors: 0,
    };
  }

  if (!data || data.length === 0) {
    return {
      totalInvestments: 0,
      totalAmountUsd: 0,
      totalCommissionUsd: 0,
      uniqueInvestors: 0,
    };
  }

  const totalAmountUsd = data.reduce((sum, inv) => sum + inv.amount_usd, 0);
  const totalCommissionUsd = data.reduce(
    (sum, inv) => sum + inv.estimated_commission_usd,
    0
  );
  const uniqueInvestors = new Set(data.map((inv) => inv.investor_wallet_address))
    .size;

  return {
    totalInvestments: data.length,
    totalAmountUsd,
    totalCommissionUsd,
    uniqueInvestors,
  };
}

/**
 * Fetches KOL commission rate from the fundraisers table by contract address.
 *
 * The smart contract doesn't store kol_commission on-chain, so we need to
 * fetch it from the database where it was saved during deployment.
 *
 * @param fundraiserAddress - The deployed fundraiser contract address
 * @returns KOL commission in basis points (e.g., 500 = 5%), or null if not found
 *
 * @example
 * ```typescript
 * const commission = await getKOLCommissionRate('0x1234...5678');
 * if (commission) {
 *   console.log(`KOL commission: ${commission / 100}%`);
 * }
 * ```
 */
export async function getKOLCommissionRate(
  fundraiserAddress: string
): Promise<number | null> {
  const { data, error } = await supabase
    .from('fundraisers')
    .select('kol_commission')
    .ilike('fundraiser_address', fundraiserAddress)
    .maybeSingle();

  if (error) {
    return null;
  }

  if (!data) {
    return null;
  }

  return data.kol_commission;
}

/**
 * Fetches fundraiser ID (UUID) from the fundraisers table by contract address.
 *
 * Used to convert blockchain contract addresses to database UUIDs for
 * foreign key relationships.
 *
 * @param fundraiserAddress - The deployed fundraiser contract address
 * @returns Fundraiser UUID, or null if not found
 *
 * @example
 * ```typescript
 * const fundraiserId = await getFundraiserIdByAddress('0x1234...5678');
 * if (fundraiserId) {
 *   console.log('Fundraiser UUID:', fundraiserId);
 * }
 * ```
 */
export async function getFundraiserIdByAddress(
  fundraiserAddress: string
): Promise<string | null> {
  const { data, error } = await supabase
    .from('fundraisers')
    .select('id')
    .ilike('fundraiser_address', fundraiserAddress)
    .maybeSingle();

  if (error) {
    return null;
  }

  if (!data) {
    return null;
  }

  return data.id;
}

// ============================================================================
// KOL PROMOTED FUNDRAISERS
// ============================================================================

/**
 * Gets all active fundraiser IDs that a KOL is promoting.
 *
 * Used to filter deals on the /deals page when user has a referral.
 * Only returns fundraisers where the promotion is active AND the
 * fundraiser is currently accepting investments.
 *
 * @param kolId - UUID of the KOL
 * @returns Array of fundraiser IDs the KOL is actively promoting
 *
 * @example
 * ```typescript
 * const fundraiserIds = await getKOLPromotedFundraiserIds('kol-uuid');
 * const kolDeals = allDeals.filter(d => fundraiserIds.includes(d.fundraiserId));
 * ```
 */
export async function getKOLPromotedFundraiserIds(
  kolId: string
): Promise<string[]> {
  const now = Math.floor(Date.now() / 1000);

  const { data, error } = await supabase
    .from('kol_project_promotions')
    .select(`
      fundraiser_id,
      fundraisers!inner (
        id,
        start_time,
        end_time
      )
    `)
    .eq('kol_id', kolId)
    .eq('is_active', true)
    .lte('fundraisers.start_time', now)
    .gte('fundraisers.end_time', now);

  if (error) {
    return [];
  }

  if (!data) {
    return [];
  }

  return data.map((row) => row.fundraiser_id);
}
